import { lazy, Suspense, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  // createRow,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import AddIcon from '@mui/icons-material/Add';
import { mkConfig, generateCsv, download } from 'export-to-csv';

const { REACT_APP_API_ENDPOINT } = process.env;

const csvConfig = mkConfig({
  fieldSeparator: ';',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
});

// ===================== API helpers =====================
async function apiGet(url) {
  const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) throw new Error('GET failed');
  return res.json();
}

async function apiPost(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('POST failed');
  return res.json();
}

// ===================== Autocomplete Depositi =====================
function GarageAutocomplete({ label = 'Deposito', value, onChange, autoFocus, disabled, placeholder }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  async function fetchGarages(q) {
    setLoading(true);
    try {
      const url = new URL(`${REACT_APP_API_ENDPOINT}/api/garages/searchForMoveVeicles`);
      if (q) url.searchParams.set('search', q);
      const data = await apiGet(url.href);
      const items = Array.isArray(data) ? data : data.items || [];
      setOptions(items);
    } catch (e) {
      console.error('garages fetch', e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Autocomplete
      openOnFocus
      value={value || null}
      onChange={(e, v) => onChange?.(v || null)}
      inputValue={inputValue}
      onInputChange={(e, v, reason) => {
        setInputValue(v);
        if (reason !== 'reset') fetchGarages(v);
      }}
      onOpen={() => fetchGarages('')}
      options={options}
      getOptionLabel={(o) => (o ? `${o.recapito || o.denominazione || ''} — ${o.localita || ''}` : '')}
      isOptionEqualToValue={(a, b) => a?._id === b?._id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder || 'Cerca deposito (Taranto, Napoli, ...)'}
          autoFocus={autoFocus}
          disabled={disabled}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option._id}>
          <Stack spacing={0.25} sx={{ width: '100%' }}>
            <Typography variant="body2" fontWeight={600}>
              {option.recapito || option.denominazione}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.localita} • {option.provincia} • {option.regione}
            </Typography>
            {option.indirizzo && (
              <Typography variant="caption" color="text.disabled">{option.indirizzo}</Typography>
            )}
          </Stack>
        </li>
      )}
    />
  );
}

// ===================== Dialog: Crea/Modifica con selezione deposito =====================
function CreateVehicleDialog({ open, onClose, onCreate, defaultValues }) {
  const [form, setForm] = useState({
    marca: defaultValues?.marca || '',
    modello: defaultValues?.modello || '',
    targa: defaultValues?.targa || '',
    garage: defaultValues?.garage || null,
  });

  const valid = form.marca && form.modello && form.targa && form.garage?._id;

  const handleSubmit = async () => {
    if (!valid) return;
    await onCreate?.({
      marca: form.marca,
      modello: form.modello,
      targa: form.targa.toUpperCase(),
      garageId: form.garage._id,
      recapito: form.garage.recapito || form.garage.denominazione,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Inserisci nuova auto</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Marca" value={form.marca} onChange={(e) => setForm((f) => ({ ...f, marca: e.target.value }))} />
          <TextField label="Modello" value={form.modello} onChange={(e) => setForm((f) => ({ ...f, modello: e.target.value }))} />
          <TextField label="Targa" value={form.targa} onChange={(e) => setForm((f) => ({ ...f, targa: e.target.value }))} />
          <GarageAutocomplete label="Deposito" value={form.garage} onChange={(g) => setForm((f) => ({ ...f, garage: g }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!valid}>Salva</Button>
      </DialogActions>
    </Dialog>
  );
}

// ===================== Dialog: Sposta veicolo/i =====================
function MoveVehicleDialog({ open, onClose, vehicles = [], onMoved }) {
  const { enqueueSnackbar } = useSnackbar();
  const isBulk = vehicles.length > 1;
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [note, setNote] = useState('');

  const queryClient = useQueryClient();
  const moveOne = useMutation({
    mutationFn: async ({ id, newGarageId, note }) =>
      apiPost(`${REACT_APP_API_ENDPOINT}/api/moveVehicles/vehicles/${id}/move`, { newGarageId, note }),
    
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mezzi'] }),
  });
  const moveBulk = useMutation({
    mutationFn: async ({ ids, newGarageId, note }) =>
      apiPost(`${REACT_APP_API_ENDPOINT}/api/moveVehicles/vehicles/bulk-move`, { vehicleIds: ids, newGarageId, note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mezzi'] }),
  });

  const submitting = moveOne.isPending || moveBulk.isPending;

  const currentDepotLabel = (() => {
    if (vehicles.length === 0) return '';
    const uniq = new Set(vehicles.map((v) => v.recapito || ''));
    return uniq.size === 1 ? [...uniq][0] : `${uniq.size} depositi diversi`;
  })();

  const handleConfirm = async () => {
    if (!selectedGarage) return;
    if (isBulk) {
      await moveBulk.mutateAsync({ ids: vehicles.map((v) => v._id), newGarageId: selectedGarage._id, note });
    } else {
      await moveOne.mutateAsync({ id: vehicles[0]._id, newGarageId: selectedGarage._id, note });
    }
    enqueueSnackbar('Spostamento completato', { variant: 'success' });
    await queryClient.invalidateQueries({ queryKey: ['mezzi'] });
    onMoved?.();
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Sposta {isBulk ? `${vehicles.length} veicoli` : 'veicolo'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Deposito attuale
            </Typography>
            <Typography variant="body2">
              {currentDepotLabel || '-'}{' '}
              {vehicles?.[0]?.targa && <>
                · <Chip size="small" label={vehicles[0].targa} />
              </>}
            </Typography>
          </Box>
          <GarageAutocomplete label="Nuovo deposito" value={selectedGarage} onChange={setSelectedGarage} autoFocus />
          <TextField label="Nota (opzionale)" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Annulla</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedGarage || submitting}>
          {submitting ? 'Spostamento…' : 'Conferma'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===================== PAGE =====================
const Example = () => {
  const [validationErrors, setValidationErrors] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveVehicles, setMoveVehicles] = useState([]);

  const columns = useMemo(
    () => [
      { accessorKey: '_id', header: '_id', enableEditing: false, size: 80 },
      {
        accessorKey: 'marca',
        header: 'Marca',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.marca,
          helperText: validationErrors?.marca,
          onFocus: () => setValidationErrors((s) => ({ ...s, marca: undefined })),
        },
      },
      {
        accessorKey: 'modello',
        header: 'Modello',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.modello,
          helperText: validationErrors?.modello,
          onFocus: () => setValidationErrors((s) => ({ ...s, modello: undefined })),
        },
      },
      {
        accessorKey: 'targa',
        header: 'targa',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.targa,
          helperText: validationErrors?.targa,
          onFocus: () => setValidationErrors((s) => ({ ...s, targa: undefined })),
        },
      },
      {
        accessorKey: 'recapito',
        header: 'deposito',
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.recapito,
          helperText: validationErrors?.recapito,
          onFocus: () => setValidationErrors((s) => ({ ...s, recapito: undefined })),
        },
        // NOTE: edit stays as plain text to avoid breaking current workflow.
        // Moving vehicles will correctly sync garageId + recapito via the dedicated dialog.
      },
      {
        id: 'actions',
        header: '',
        Cell: ({ row, table }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Modifica">
              <IconButton onClick={() => table.setEditingRow(row)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Elimina">
              <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sposta">
              <IconButton color="primary" onClick={() => { setMoveVehicles([row.original]); setMoveOpen(true); }}>
                <DriveFileMoveIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
        size: 120,
      },
    ],
    [validationErrors]
  );

  // hooks API
  const { mutateAsync: createAutomezzo, isPending: isCreatingAutomezzo } = useCreateAutomezzo();
  const { data: fetchedAutomezzi = [], isError: isLoadingAutomezziError, isFetching: isFetchingAutomezzi, isLoading: isLoadingAutomezzi } = useGetAutomezzi();
  const { mutateAsync: updateAutomezzo, isPending: isUpdatingAutomezzo } = useUpdateAutomezzo();
  const { mutateAsync: deleteAutomezzo, isPending: isDeletingAutomezzo } = useDeleteAutomezzo();
  const queryClient = useQueryClient();

  // CREATE via dialog (keeps inline table stable)
  const handleCreateDialog = async (payload) => {
    await createAutomezzo(payload);
    setCreateOpen(false);
  };

  // CREATE (inline fallback, still supported)
  const handleCreateAutomezzo = async ({ values, table }) => {
    const newValidationErrors = validateAutomezzo(values);
    if (Object.values(newValidationErrors).some((e) => e)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createAutomezzo(values);
    table.setCreatingRow(null);
  };

  // UPDATE inline
  const handleSaveAutomezzo = async ({ values, table }) => {
    const newValidationErrors = validateAutomezzo(values);
    if (Object.values(newValidationErrors).some((e) => e)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await updateAutomezzo(values);
    table.setEditingRow(null);
  };

  // DELETE
  const openDeleteConfirmModal = (row) => {
    if (window.confirm('Sei sicuro di voler eliminare questo automezzo?')) {
      deleteAutomezzo(row.original._id);
    }
  };

  // EXPORT
  const handleExportRows = (rows, fileName = 'report-parco-auto') => {
    const rowData = rows.map((row) => row.original);
    const customConfig = { ...csvConfig, filename: fileName };
    const csv = generateCsv(customConfig)(rowData);
    download(customConfig)(csv);
  };

  const table = useMaterialReactTable({
    columns,
    data: fetchedAutomezzi,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    enableEditing: true,
    enableRowSelection: true,
    getRowId: (row) => row._id,
    muiToolbarAlertBannerProps: isLoadingAutomezziError ? { color: 'error', children: 'Errore nel caricamento dei dati' } : undefined,
    muiTableContainerProps: { sx: { minHeight: '500px' } },
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateAutomezzo,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleSaveAutomezzo,
    renderTopToolbarCustomActions: ({ row, table }) => (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Inserisci nuova auto
        </Button>
        <Button
          variant="outlined"
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Esporta Tabella
        </Button>
        <Button
          variant="outlined"
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => handleExportRows(table.getSelectedRowModel().rows, 'report-parco-auto-selezione')}
          startIcon={<FileDownloadIcon />}
        >
          Esporta Righe Selezionate
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<DriveFileMoveIcon />}
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          onClick={() => {
            const selected = table.getSelectedRowModel().rows.map((r) => r.original);
            setMoveVehicles(selected);
            setMoveOpen(true);
          }}
        >
          Sposta selezionati
        </Button>
      </Stack>
    ),
    state: {
      isLoading: isLoadingAutomezzi,
      isSaving: isCreatingAutomezzo || isUpdatingAutomezzo || isDeletingAutomezzo,
      showAlertBanner: isLoadingAutomezziError,
      showProgressBars: isFetchingAutomezzi,
      showLoadingOverlay: isFetchingAutomezzi,
    },
  });

  const handleMoved = () => {
    // 🔁 forza solo il refetch remoto
    queryClient.invalidateQueries({ queryKey: ['mezzi'] });
    // reset selezione righe
    table.resetRowSelection?.();
  };

  return (
    <>
      <MaterialReactTable table={table} />

      <CreateVehicleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={async (payload) => {
          await createAutomezzo(payload);
          setCreateOpen(false);
        }}
      />

      <MoveVehicleDialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
    vehicles={moveVehicles}
    onMoved={(p) => { handleMoved(p); table.resetRowSelection(); }}
      />
    </>
  );
};

// ===================== React Query hooks CRUD =====================
function useCreateAutomezzo() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (automezzo) => {
      const response = await fetch(`${REACT_APP_API_ENDPOINT}/api/flotta/mezzi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(automezzo),
      });
      if (!response.ok) throw new Error('Failed to create automezzo');
      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Inserimento completato', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['mezzi'] });
    },
    onError: (error) => {
      console.error('Create error:', error);
    },
  });
}

function useGetAutomezzi() {
  return useQuery({
    queryKey: ['mezzi'],
    queryFn: async () => {
      const fetchURL = new URL(`${REACT_APP_API_ENDPOINT}/api/flotta/mezzi`);
      const response = await fetch(fetchURL.href);
      const json = await response.json();
      return json.data;
    },
    refetchOnWindowFocus: false,
  });
}

function useUpdateAutomezzo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (automezzo) => {
      const response = await fetch(`${REACT_APP_API_ENDPOINT}/api/flotta/mezzi/${automezzo._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(automezzo),
      });
      if (!response.ok) throw new Error('Failed to update automezzo');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mezzi'] });
    },
    onError: (error) => {
      console.error('Update error:', error);
    },
  });
}

function useDeleteAutomezzo() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (automezzoId) => {
      const response = await fetch(`${REACT_APP_API_ENDPOINT}/api/flotta/mezzi/${automezzoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Failed to delete automezzo');
      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Eliminazione completata', { variant: 'success' });
       queryClient.invalidateQueries({ queryKey: ['mezzi'] });
    },
    onError: (error) => {
      console.error('Delete error:', error);
    },
  });
}

// ===================== App wrapper =====================
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({ default: d.ReactQueryDevtools }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false, retry: 2 },
    mutations: { retry: 1 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
    </QueryClientProvider>
  );
}

// ===================== Validation (recapito text still required inline) =====================
const validateRequired = (value) => !!String(value || '').length;
function validateAutomezzo(automezzo) {
  return {
    marca: !validateRequired(automezzo.marca) ? 'Marca è richiesta' : '',
    modello: !validateRequired(automezzo.modello) ? 'Modello è richiesto' : '',
    targa: !validateRequired(automezzo.targa) ? 'Targa è richiesta' : '',
    recapito: !validateRequired(automezzo.recapito) ? 'Recapito è richiesto' : '',
  };
}
